/* eslint-disable no-await-in-loop */
import React, { useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import { graphql } from 'react-apollo';
import { useTranslation } from 'react-i18next';
import { compose, mapProps } from 'recompose';
import ErrorIcon from '@material-ui/icons/Error';
import _ from 'lodash';

import { getTemplateAttachmentUploadToken, publishTemplateAttachment, checkTemplateAttachmentUploadStatus } from 'graphql/operations';
import FileSelector from 'components/common/FileSelector/FileSelector';
import DotLoader from 'components/common/Loader/DotLoader/DotLoader';
import { showErrorMessage } from 'components/common/Alert/Alert';
import { promiseTimeout, md5, hasValue, getFileExtension, isInvalidFilename } from 'utils/functions';
import { getQueryFilterVariables } from 'selectors/assessmentFilters/selector';
import createError from 'utils/validations/createError';

import './UploadAttachment.scss';

const MB_TO_BYTES = 1048576; // Number of bytes in 1MB

const readFile = file => {
  const p = new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      let binary = '';
      const bytes = new Uint8Array(reader.result);
      const length = bytes.byteLength;
      for (let i = 0; i < length; i += 1) {
        binary += String.fromCharCode(bytes[i]);
      }
      const checksum = md5(binary);
      resolve({ checksum });
    };
    reader.onerror = event => {
      reader.abort();
      reject(event);
    };

    reader.readAsArrayBuffer(file);
  });
  return p;
};

function getAttachmentTotal(attachments) {
  return hasValue(attachments) ? attachments.length : 0;
}

export const UploadTemplateAttachment = props => {
  const {
    templateId,
    recordId,
    question: { attachmentValue, maxFileSize, maxAttachmentsAllowed, minAttachmentsAllowed }
  } = props;
  const { t } = useTranslation();
  const [validationErrors, setValidationErrors] = useState([]);

  const total = getAttachmentTotal(attachmentValue);
  const [currentTotal, setCurrentTotal] = useState(total);

  if (total !== currentTotal) {
    if (total < currentTotal) {
      // Remove validations if number of attachments decreased (i.e. User deleted attachment)
      setValidationErrors([]);
    }
    setCurrentTotal(total);
  }

  const [numberOfFilesMsg, setNumberOfFilesMsg] = useState(null);

  useEffect(() => {
    // Information about number of files that can be uploaded
    const hasMin = hasValue(minAttachmentsAllowed);
    const hasMax = hasValue(maxAttachmentsAllowed);

    if (hasMax && hasMin) {
      if (minAttachmentsAllowed === maxAttachmentsAllowed) {
        setNumberOfFilesMsg(t('validation.attachments.total', { number: minAttachmentsAllowed }));
      } else {
        setNumberOfFilesMsg(t('validation.attachments.rangeFromTo', { from: minAttachmentsAllowed, to: maxAttachmentsAllowed }));
      }
    } else if (hasMax) {
      setNumberOfFilesMsg(t('validation.attachments.rangeMax', { max: maxAttachmentsAllowed }));
    } else if (hasMin) {
      setNumberOfFilesMsg(t('validation.attachments.rangeMin', { min: minAttachmentsAllowed }));
    }
  }, [maxAttachmentsAllowed, minAttachmentsAllowed, numberOfFilesMsg, t]);

  const [isLoading, setIsLoading] = useState(false);

  const handleUploadError = err => {
    console.error('error uploading file. error:', err);
    if (err === 'INVALID') showErrorMessage(t('assessment.attachment.upload.invalidMsg'));
    else showErrorMessage(t('assessment.attachment.upload.errorMsg'));
  };

  const maxFileSizeInMB = maxFileSize * MB_TO_BYTES;
  const onSelectedFiles = async files => {
    // Validate input
    let validFiles = [];
    let invalidFiles = [];
    let errors = [];
    let fileExtension = '';
    let fileName = '';
    const allowedFileTypes = ['doc', 'docx', 'pdf', 'txt', 'rtf', 'ppt', 'pptx', 'msg', 'xls', 'xlsx', 'csv', 'png', 'gif', 'jpg', 'jpeg', 'bmp'];

    files.forEach(file => {
      fileName = file.name.trim();
      fileExtension = getFileExtension(fileName);
      // Remove files that exceed max size or contain invalid type
      if (fileName === '') {
        const errorMsg = createError('validation.attachments.empty');
        invalidFiles.push(errorMsg);
      } else if (isInvalidFilename(fileName)) {
        const errorMsg = createError('validation.attachments.invalidFilename');
        invalidFiles.push(errorMsg);
      } else if (fileExtension === '') {
        const errorMsg = createError('validation.attachments.type', { name: fileName });
        invalidFiles.push(errorMsg);
      } else if (!allowedFileTypes.includes(fileExtension)) {
        const errorMsg = createError('validation.attachments.unsupported', { name: fileName });
        invalidFiles.push(errorMsg);
      } else if (hasValue(maxFileSize) && file.size > maxFileSizeInMB) {
        const errorMsg = createError('validation.attachments.sizeMb', { name: fileName, size: maxFileSize });
        invalidFiles.push(errorMsg);
      } else {
        validFiles.push(file);
      }
    });

    // Check and drop files if number of files exceeds max allowed
    if (hasValue(maxAttachmentsAllowed)) {
      const capacity = maxAttachmentsAllowed - currentTotal;
      const isOverCapacity = validFiles.length > capacity;
      if (isOverCapacity) {
        const kept = _.take(validFiles, capacity);
        let dropped = _.takeRight(validFiles, validFiles.length - capacity);
        validFiles = kept;
        dropped = dropped.map(file => createError('validation.attachments.tooMany', { name: file.name }));
        invalidFiles = invalidFiles.concat(dropped);
      }
    }

    if (invalidFiles.length) {
      errors = errors.concat(invalidFiles);
    }

    // Check if user did not upload enough files
    if (hasValue(minAttachmentsAllowed)) {
      const isUnder = currentTotal + validFiles.length < minAttachmentsAllowed;
      if (isUnder) {
        // Display this error above any other error
        errors.unshift(createError('validation.attachments.tooFew', { min: minAttachmentsAllowed }));
      }
    }

    // Show Errors
    setValidationErrors(errors);

    // Upload valid files
    setIsLoading(true);
    props.incrementAttachmentUploadCount();

    for (let i = 0; i < validFiles.length; i += 1) {
      const file = validFiles[i];

      try {
        const { checksum } = await readFile(file);

        // 1) get the s3 URL to upload the attachment.
        const {
          data: {
            getTemplateAttachmentUploadToken: { id: attachmentId, url: uploadUrl, fields: awsUploadFields }
          }
        } = await props.getTemplateAttachmentUploadToken({
          variables: {
            templateId,
            recordId,
            fieldId: props.question.id,
            filename: file.name,
            mediaType: getFileExtension(file.name),
            md5Checksum: checksum
          }
        });

        // 2) Upload the file to S3
        const formData = new FormData();
        awsUploadFields.forEach(config => {
          formData.append(config.name, config.value);
        });
        formData.append('file', file);

        const fetchResponse = await fetch(uploadUrl, {
          method: 'POST',
          body: formData
        });

        if (fetchResponse.ok === true) {
          // 3) Notify BE the file has been uploaded
          await props.publishTemplateAttachment({ variables: { templateId, recordId, attachmentId } }); // TODO: Check why we do not pass assessmentId in Assessments...

          // 4) Check upload status
          const checkPublishStatus = () =>
            props
              .checkTemplateAttachmentUploadStatus({ variables: { templateId, recordId, attachmentId } })
              .then(
                async ({
                  data: {
                    checkTemplateAttachmentUploadStatus: {
                      eventData: {
                        attachmentUploadStatus: { state }
                      }
                    }
                  }
                }) => {
                  if (state === 'IN_PROGRESS') {
                    return promiseTimeout(3000)().then(checkPublishStatus);
                  }

                  if (state === 'FAILED') {
                    handleUploadError(state);
                  }
                  if (state === 'INVALID') {
                    handleUploadError(state);
                  }
                  return Promise.resolve(state);
                }
              )
              .catch(handleUploadError);

          await checkPublishStatus();
        } else {
          handleUploadError('Error uploading to s3');
        }
      } catch (err) {
        handleUploadError(err);
      }
    }

    setIsLoading(false);
    props.decrementAttachmentUploadCount();
  };

  const errors = validationErrors.map(
    error =>
      error?.key && (
        <div key={error.key} className="attachment-validation-error">
          <ErrorIcon />
          <div className="error-message">{t(error.key, { ...error.args })}</div>
        </div>
      )
  );

  const { disabled: propsDisabled } = props;
  const disabled = propsDisabled || (hasValue(maxAttachmentsAllowed) && currentTotal >= maxAttachmentsAllowed);

  const dzText = (
    <div>
      <div>
        {t('assessment.attachment.dropHere')}
        &nbsp;
        <span className="click-to">{t('common.click')}</span>
        &nbsp;
        {t('assessment.attachment.toUpload')}
      </div>
      <div>
        {numberOfFilesMsg}
        {numberOfFilesMsg && maxFileSize && ' '}
        {maxFileSize && t('assessment.attachment.maxFileSize', { size: maxFileSize })}
      </div>
    </div>
  );

  // Render
  return (
    <>
      <div className="rsa-upload-attachment">
        {isLoading ? (
          <DotLoader content={t('assessment.attachment.upload.uploading')} />
        ) : (
          <FileSelector
            onSelectedFiles={onSelectedFiles}
            disabled={disabled}
            dragActiveText={<div>{t('assessment.attachment.activeDropZone')}</div>}
            dropZoneText={dzText}
          />
        )}
      </div>
      {errors}
    </>
  );
};

export const UploadTemplateAttachmentHOC = compose(
  withRouter,
  graphql(getTemplateAttachmentUploadToken, { name: 'getTemplateAttachmentUploadToken' }),
  graphql(publishTemplateAttachment, { name: 'publishTemplateAttachment' }),
  graphql(checkTemplateAttachmentUploadStatus, { name: 'checkTemplateAttachmentUploadStatus' }),
  mapProps(props => ({
    disabled: props.disabled,
    templateId: props.assessmentId,
    recordId: props.recordId,
    assessmentFieldsFilter: getQueryFilterVariables(props),
    getTemplateAttachmentUploadToken: props.getTemplateAttachmentUploadToken,
    publishTemplateAttachment: props.publishTemplateAttachment,
    checkTemplateAttachmentUploadStatus: props.checkTemplateAttachmentUploadStatus,
    question: props.question,
    incrementAttachmentUploadCount: props.incrementAttachmentUploadCount,
    decrementAttachmentUploadCount: props.decrementAttachmentUploadCount
  }))
);

export default UploadTemplateAttachmentHOC(UploadTemplateAttachment);
